import { useMemo } from 'react';

import classNames from 'classnames';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ImageProxy from '@next-image-proxy';

import { LaunchpadProject } from '@api';
import { LAUNCHPAD_PROJECT_LINK_TYPE } from '@api';

import ExternalLink from '@uikit/components/ExternalLink/ExternalLink';
import { Discord } from '@uikit/icons/Discord';
import { MediumNeutral } from '@uikit/icons/MediumNeutral';
import { Telegram } from '@uikit/icons/Telegram';
import { XNeutral } from '@uikit/icons/XNeutral';
import { YoutubeNeutral } from '@uikit/icons/YoutubeNeutral';

import LaunchInfoMultiPool from '@launchpad/components/pages/Project/ui/LaunchInfo/LaunchInfoMultiPool';
import PoolCard from '@launchpad/components/pages/Project/ui/OptionsCard/PoolCard';

type SocialType = 'TWITTER' | 'TELEGRAM' | 'DISCORD' | 'MEDIUM' | 'YOUTUBE';

const socialsIcons = {
  TWITTER: <XNeutral className="icon-xs" />,
  TELEGRAM: <Telegram className="icon-xs" />,
  DISCORD: <Discord className="icon-xs" />,
  MEDIUM: <MediumNeutral className="icon-sm" />,
  YOUTUBE: <YoutubeNeutral className="icon-sm" />,
};

export const ProjectInfoSkeleton = ({
  isMultiPoolPrefetched,
  isHaveSale,
}: {
  isMultiPoolPrefetched: boolean;
  isHaveSale: boolean;
}) => {
  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex justify-start gap-2 items-center ">
        <div className="skeleton-block w-12 h-12 rounded-full" />
        <p className="skeleton-text w-[200px] h-10" />
      </div>
      <div className="flex justify-between flex-col lg:flex-row lg:items-start">
        <div className="flex flex-col lg:max-w-[600px] w-full gap-6">
          <div className="skeleton-block pt-[56.25%]"></div>
          <div className="flex flex-col gap-2">
            <p className="skeleton-text" />
            <p className="skeleton-text" />
          </div>
        </div>
        {isHaveSale && (
          <div
            className={classNames('w-[375px] skeleton-block', {
              'h-[225px]': isMultiPoolPrefetched,
              'h-[485px]': !isMultiPoolPrefetched,
            })}
          />
        )}
      </div>
    </div>
  );
};

const ProjectInfo = ({ project }: { project: LaunchpadProject }) => {
  const socials = useMemo(() => {
    const links =
      project?.attributes?.links?.filter((link) => {
        return (
          link.type !== LAUNCHPAD_PROJECT_LINK_TYPE.DOCUMENT &&
          link.type !== LAUNCHPAD_PROJECT_LINK_TYPE.WEBSITE &&
          Object.keys(socialsIcons).includes(link.type)
        );
      }) || [];
    return links?.map((link) => ({
      link: link.link,
      type: link.type,
      icon: socialsIcons[link.type as SocialType],
    }));
  }, [project]);

  const links = useMemo(() => {
    return (
      project?.attributes?.links?.filter((link) => {
        return (
          link.type === LAUNCHPAD_PROJECT_LINK_TYPE.DOCUMENT ||
          link.type === LAUNCHPAD_PROJECT_LINK_TYPE.WEBSITE
        );
      }) || []
    );
  }, [project]);

  const showContacts = Boolean(links.length) || Boolean(socials.length);
  const isMultiPool = project?.salePool && project?.salePool?.length > 1;
  const isSinglePool = project?.salePool && project?.salePool?.length === 1;
  const bannerUrls = project?.attributes.bannerUrls || [];
  const showSlider = Boolean(bannerUrls.length);

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex justify-start gap-2 items-center ">
        <ImageProxy
          src={project?.attributes?.logoUrl}
          alt={`${project.attributes.name} logo`}
          className="w-12 rounded-full"
          width={48}
          height={48}
          quality={100}
        />

        <h1 className="t-h2 text-[32px]">{project?.attributes?.name}</h1>
      </div>

      <div className="flex justify-between flex-col lg:flex-row gap-6 lg:items-start">
        <div className="flex flex-col gap-4 sm:gap-6 lg:max-w-[600px] w-full">
          {showSlider && (
            <Swiper
              className={classNames('w-full static')}
              loop
              grabCursor
              spaceBetween={16}
              slidesPerView={1}
              modules={[Autoplay, Pagination]}
              autoplay={{
                delay: 5000,
                pauseOnMouseEnter: true,
              }}
              pagination={{
                el: '.launchpad-project-swiper-pagination',
                type: 'bullets',
                clickable: true,
                bulletClass:
                  'block w-3 h-3 rounded-full aspect-square bg-primary-300 hover:cursor-pointer',
                bulletActiveClass: 'bg-primary-700',
              }}
            >
              <div className="launchpad-project-swiper-pagination w-full flex justify-center items-center gap-2 mt-4 sm:mt-6"></div>

              {bannerUrls?.map((bannerSrc, index) => {
                return (
                  <SwiperSlide key={index} className={classNames('select-none w-full')}>
                    <ImageProxy
                      className="w-full object-cover aspect-[16/9] flex justify-center items-center overflow-hidden rounded-lg"
                      width={1920}
                      height={1080}
                      alt={`${project.attributes.name} slide ${index}`}
                      src={bannerSrc}
                    />
                  </SwiperSlide>
                );
              })}
            </Swiper>
          )}

          {!showSlider && (
            <ImageProxy
              className="w-full object-cover aspect-[16/9] flex justify-center items-center overflow-hidden rounded-lg"
              width={1920}
              height={1080}
              alt={project.attributes.name}
              src={project?.attributes?.bannerUrl}
            />
          )}

          <div>
            <p className="font-semibold">{project?.attributes?.description}</p>
            {showContacts && (
              <div className="mt-2 flex justify-between flex-col md:flex-row gap-4">
                <div className="flex justify-start gap-4">
                  {links.map((link, index) => (
                    <ExternalLink
                      key={index}
                      href={link.link}
                      className="rounded-full py-2 px-4 bg-primary-50 text-primary-700 underline"
                    >
                      {link.title}
                    </ExternalLink>
                  ))}
                </div>

                <div className="flex md:justify-end items-center gap-4">
                  {socials.map((social) => (
                    <ExternalLink
                      key={social.type}
                      href={social.link}
                      className="rounded-full bg-primary-50 text-neutral-700 w-8 h-8 flex items-center justify-center"
                    >
                      {social.icon}
                    </ExternalLink>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {isMultiPool && <LaunchInfoMultiPool className="lg:min-w-[23.4375rem]" project={project} />}

        {isSinglePool && (
          <PoolCard
            projectSlug={project.attributes.slug}
            pool={project?.salePool[0]}
            token={project.attributes.ticker}
            className="w-full lg:min-w-[375px] lg:max-w-[375px]"
          />
        )}
      </div>
    </div>
  );
};

export default ProjectInfo;
